import { ComponentBase } from "../../../core/ComponentBase.js";
import { ChatViewHeader } from "./ChatViewHeader.js";
import { MessageArea } from "./MessageArea.js";
import { MessageInput } from "./MessageInput.js";

export class ChatView extends ComponentBase {
    static styles = `
        .chat-view-wrapper {
            display: flex;
            flex-direction: column;
            height: 100%; /* Fill parent pane */
            overflow: hidden; /* Important: Parent handles scroll if needed */
            background-color: var(--bs-body-bg);
            position: relative; /* CRUCIAL: For positioning the scroll-to-bottom button */
        }
        .chat-view-scroll-to-bottom-btn {
            position: absolute;
            /* Adjust bottom based on MessageInput's typical height + desired margin */
            /* Example: 60px (input) + 10px (margin) = 70px */
            bottom: 100px; 
            right: 1rem;  /* Consistent padding */
            z-index: 100; 
            background-color: var(--bs-primary);
            color: white; /* Ensure good contrast */
            border: none;
            border-radius: 50%;
            width: 40px;
            height: 40px;
            font-size: 1.2rem;
            display: none; /* Hidden by default */
            align-items: center; /* Center icon */
            justify-content: center; /* Center icon */
            box-shadow: 0 2px 5px rgba(0,0,0,0.2);
            transition: opacity 0.2s ease-in-out, transform 0.2s ease-in-out; /* Smooth transitions */
        }
        .chat-view-scroll-to-bottom-btn.visible {
            display: flex; /* Use flex to center icon */
            opacity: 1;
            transform: scale(1);
        }
        .chat-view-scroll-to-bottom-btn:not(.visible) {
            opacity: 0;
            transform: scale(0.8);
        }
    `;

    constructor(config) { // Expects { appClient, instanceId, myContactId }
        super(config);
        this.header = null;
        this.messageArea = null;
        this.messageInput = null;

        this.activeConversationId = null;
        this.activeConversationData = null; // Store full conversation/channel object
        this.myContactId = this.config.myContactId;

        this.scrollToBottomButtonElement = null; // For the button DOM element
        this.isScrollButtonVisible = false;      // To manage its state for hysteresis

        this.applyStyles();
    }

    async initialize() {
        await super.initialize();
        this.container = document.createElement('div');
        this.container.className = 'chat-view-wrapper';

        this.header = new ChatViewHeader({
            appClient: this.appClient,
            instanceId: `${this.config.instanceId}-header`,
            conversationData: null
        });
        await this.header.initialize();
        this.header.on('backClicked', () => this.emit('backClicked'));

        this.messageArea = new MessageArea({
            appClient: this.appClient,
            instanceId: `${this.config.instanceId}-msgarea`,
            myContactId: this.myContactId
        });
        await this.messageArea.initialize();
        this.messageArea.on('loadOlderMessages', this.loadOlderMessagesForActiveChat.bind(this));
        this.messageArea.on('scrollButtonStateChange', this.handleScrollButtonStateChange.bind(this));

        this.messageInput = new MessageInput({
            appClient: this.appClient,
            instanceId: `${this.config.instanceId}-msginput`,
        });
        await this.messageInput.initialize();
        this.messageInput.on('messageSent', this.handleMessageInput.bind(this));

        this.container.appendChild(this.header.render());
        this.container.appendChild(this.messageArea.render());
        this.container.appendChild(this.messageInput.render());

        this.scrollToBottomButtonElement = document.createElement('button');
        this.scrollToBottomButtonElement.className = 'chat-view-scroll-to-bottom-btn';
        this.scrollToBottomButtonElement.innerHTML = '<i class="bi bi-arrow-down"></i>';
        this.scrollToBottomButtonElement.addEventListener('click', () => {
            if (this.messageArea) {
                this.messageArea.scrollToBottom(true);
            }
        });
        this.container.appendChild(this.scrollToBottomButtonElement); // Append to ChatView's main container

    }

    handleScrollButtonStateChange({ visible }) {
        if (this.scrollToBottomButtonElement) {
            if (visible && !this.isScrollButtonVisible) {
                this.scrollToBottomButtonElement.classList.add('visible');
                this.isScrollButtonVisible = true;
            } else if (!visible && this.isScrollButtonVisible) {
                this.scrollToBottomButtonElement.classList.remove('visible');
                this.isScrollButtonVisible = false;
            }
        }
    }

    setMyContactId(contactId) {
        this.myContactId = contactId;
        this.messageArea.myContactId = contactId;
    }

    async setActiveConversation(conversationId) {
        if (this.activeConversationId === conversationId) return;
        this.activeConversationId = conversationId;

        if (!conversationId) {
            this.activeConversationData = null;
            this.header.updateConversationData(null);
            this.messageArea.setMessages([], false, this.myContactId);
            return;
        }

        try {
            const allConversations = this.appClient.getSharedData('allUserConversations') || [];
            this.activeConversationData = allConversations.find(c => c.channelId === conversationId);

            if (!this.activeConversationData) {
                this.log.warn(`Conversation data for ${conversationId} not found.`);
                const channelData = await this.appClient.rpcClient.call('MessagingRpc.getChannelById',
                    { channelId: conversationId },
                    { projectId: this.appClient.getSharedData('selectedProjectId') }
                );
                if (channelData) {
                    this.activeConversationData = channelData;
                } else {
                    this.log.error(`Failed to fetch conversation details for ${conversationId}`);
                    this.activeConversationData = { displayName: "Chat", channelId: conversationId };
                }
            }

            this.header.updateConversationData(this.activeConversationData);
            this.messageArea.isGroupChat = this.activeConversationData.channelType === 'group';
            await this.loadMessagesForActiveChat(true);

        } catch (error) {
            this.log.error(`Error setting active conversation ${conversationId}:`, error);
            this.appClient.notifier.error("Failed to load chat.");
        }
    }

    async loadMessagesForActiveChat(isInitialLoad = false) {
        if (!this.activeConversationId) return;

        let pageToLoad;
        if (isInitialLoad) {
            pageToLoad = 1;
        } else {
            // Calculate the number of full pages already loaded
            const fullPagesLoaded = Math.floor(this.messageArea.messages.length / 20);
            pageToLoad = fullPagesLoaded + 1;
        } this.messageArea.setLoadingOlder(true, this.messageArea.hasOlderMessages);

        try {
            const result = await this.appClient.rpcClient.call('MessagingRpc.getMessages', {
                conversationId: this.activeConversationId,
                page: pageToLoad,
                pageSize: 20
            }, { projectId: this.appClient.getSharedData('selectedProjectId') });

            const newMessages = result.data || [];
            if (isInitialLoad) {
                this.messageArea.setMessages(newMessages, this.activeConversationData.channelType === 'group', this.myContactId);
            } else {
                newMessages.forEach(msg => this.messageArea.addMessage(msg, true));
            }
            this.messageArea.setLoadingOlder(false, newMessages.length > 0 && result.totalCount > this.messageArea.messages.length);

            // --- Mark messages as read after loading them ---
            if (newMessages.length > 0) {
                const unreadMessageIds = newMessages.filter(msg => !msg.isOwnMessage && (!msg.readBy || !msg.readBy.some(r => r.contactId === this.myContactId))).map(msg => msg._id);
                if (unreadMessageIds.length > 0) {
                    this.emit('messagesViewed', { conversationId: this.activeConversationId, messageIds: unreadMessageIds });
                }
            }
            // --- End Mark as Read ---

        } catch (error) {
            this.log.error(`Error loading messages for ${this.activeConversationId}:`, error);
            this.appClient.notifier.error("Failed to load messages.");
            this.messageArea.setLoadingOlder(false, false);
        }
    }

    async loadOlderMessagesForActiveChat() {
        await this.loadMessagesForActiveChat(false);
    }

    handleMessageInput({ text, attachments }) {
        if (!this.activeConversationId || !this.activeConversationData) return;
        this.emit('messageSent', {
            recipientType: this.activeConversationData.channelType === 'direct_message' ? 'contact' : 'channel',
            recipientId: this.activeConversationData.channelType === 'direct_message'
                ? this.activeConversationData.members.find(m => m.contactId !== this.myContactId)?.contactId
                : this.activeConversationData.channelId,
            messageContent: text,
            attachments: attachments || [],
            messageType: attachments && attachments.length > 0 ? 'file' : 'text'
        });
    }

    handleIncomingMessage(messageData) {
        if (messageData.conversationId === this.activeConversationId) {
            this.messageArea.addMessage(messageData, false);
            if (!messageData.isOwnMessage) {
                this.emit('messagesViewed', { conversationId: this.activeConversationId, messageIds: [messageData._id] });
            }
        }
    }

    updateMessageStatus(tempId, serverId, status) {
        this.messageArea.updateMessageStatus(tempId, serverId, status);
    }

    handleConversationUpdate(updatedConvData) {
        if (this.activeConversationId === updatedConvData.channelId) {
            this.activeConversationData = { ...this.activeConversationData, ...updatedConvData };
            this.header.updateConversationData(this.activeConversationData);
        }
    }

    updateTypingIndicator(typingData) {
        // typingData: { conversationId, contactId, displayName, isTyping }
        if (this.header) { // Assuming header will display it
            this.header.updateTypingStatus(typingData);
        }
        // Alternatively, if MessageArea or another sub-component handles it:
        // if (this.messageArea) {
        //     this.messageArea.updateTypingIndicator(typingData);
        // }
    }

    render() {
        return this.container;
    }

    destroy() {
        this.header?.destroy();
        this.messageArea?.destroy();
        this.messageInput?.destroy();

        this.messageArea?.off('scrollButtonStateChange', this.handleScrollButtonStateChange);
        if (this.scrollToBottomButtonElement) {
            this.scrollToBottomButtonElement.removeEventListener('click', () => { });
            this.scrollToBottomButtonElement.remove();
        }

        super.destroy();
    }
}
